import { z } from 'zod/v4';

// don't wanna enforce anything here in case their SDK changes etc..
// we could have used the blockaid validation result from the DO but I'm worried it will change if their API changes
export const blockaidValidationResultSchema = z.any();

// Actual gas details (post-execution)
export const evmGasDetailsActualSchema = z.object({
  type: z.literal('evm'),
  gasUsed: z.string(),
  gasUsedL1: z.string().optional().describe('Only for OP chains'),
});

export const svmGasDetailsActualSchema = z.object({
  type: z.literal('svm'),
  computeUnitsConsumed: z.string(),
});

export const gasDetailsActualSchema = z.discriminatedUnion('type', [
  evmGasDetailsActualSchema,
  svmGasDetailsActualSchema,
]);

// Quote gas details (pre-execution estimates)
export const evmGasDetailsQuoteSchema = z.object({
  type: z.literal('evm'),
  maxFeePerGas: z.string(),
  maxPriorityFeePerGas: z.string(),
  callGasLimit: z.string(),
  preVerificationGas: z.string(),
  verificationGasLimit: z.string(),
});

export const svmGasDetailsQuoteSchema = z.object({
  type: z.literal('svm'),
  computeUnitPrice: z.string(),
  computeUnitsConsumed: z.string(),
});
export type SvmGasDetailsQuote = z.infer<typeof svmGasDetailsQuoteSchema>;

export const gasDetailsQuoteSchema = z.discriminatedUnion('type', [
  evmGasDetailsQuoteSchema,
  svmGasDetailsQuoteSchema,
]);

export const TRANSACTION_TYPES = [
  'evm:userOperation',
  'evm:transaction',
  'svm:transaction',
] as const;
export const transactionTypeSchema = z.enum(TRANSACTION_TYPES);

export const TRANSACTION_STATUSES = [
  'requested',
  'submitted',
  'submit_failed',
  'confirmed',
  // If a transaction never actually confirms (fails simulation, expires, other)
  'confirm_failed',
] as const;
export const transactionStatusSchema = z.enum(TRANSACTION_STATUSES);

export const TRANSACTION_INTENTS = [
  'mint',
  'stake',
  'swap',
  'native_transfer',
  'token_transfer',
  'approval', // Blockaid has this for both evm and solana (https://docs.blockaid.io/reference/solana_scan_post_transaction_post_transaction_scan_post-1)
] as const;
export type TransactionIntent = (typeof TRANSACTION_INTENTS)[number];
export const transactionIntentsSchema = z.array(z.enum(TRANSACTION_INTENTS));

// Transaction events (e.g., token account initialization/closing)
export const TRANSACTION_EVENTS = [
  'token_account_initialization',
  'token_account_closing',
] as const;
export type TransactionEvent = (typeof TRANSACTION_EVENTS)[number];
export const transactionEventsSchema = z.array(z.enum(TRANSACTION_EVENTS));

// Zod schema for signing request modifications
export const evmApprovalModificationSchema = z.object({
  type: z.literal('evm:approval'),
  token: z.string(),
  spender: z.string(),
  amount: z.string(),
});
export type EvmApprovalModification = z.infer<
  typeof evmApprovalModificationSchema
>;

export const signingRequestModificationSchema = z.discriminatedUnion('type', [
  evmApprovalModificationSchema,
  // Future: other modification types (svm:*, etc.)
]);
export type SigningRequestModification = z.infer<
  typeof signingRequestModificationSchema
>;

export const transactionMetadataSchema = z.object({
  type: z.literal('transaction_metadata_v1'),
  intents: transactionIntentsSchema.optional(),
  events: transactionEventsSchema.optional(),
  blockaidValidationResult: blockaidValidationResultSchema.optional(),
  modifications: z.array(signingRequestModificationSchema).optional(),
  parsingResult: z.any().optional(),
});
export type TransactionMetadata = z.infer<typeof transactionMetadataSchema>;

// Schema definitions
// The parsed field can contain:
// 1. Parsing results (ERC7730, SVM, Message)
// 2. Transaction metadata with intents, events, and blockaid validation result
export const parsedSchema = z.union([
  z.any(), // Parsing results (ERC7730, SVM, Message)
  transactionMetadataSchema,
]);

export const SPONSORSHIP_MODES = ['sponsored', 'native', 'account'] as const;
export type SponsorshipMode = (typeof SPONSORSHIP_MODES)[number];
export const sponsorshipModeSchema = z.enum(SPONSORSHIP_MODES);

export const GAS_RATINGS = ['normal', 'high'] as const;
export const gasRatingSchema = z.enum(GAS_RATINGS);
export type GasRating = (typeof GAS_RATINGS)[number];

export const transactionSchema = z.object({
  id: z.string(),
  bannerId: z.string().optional(),
  type: transactionTypeSchema,
  dappSessionId: z.string(),
  chainId: z.string(),
  // TODO: backfill
  walletAddress: z.string().nullish(),
  status: transactionStatusSchema,
  parsed: parsedSchema,
  submitId: z
    .string()
    .nullish()
    .describe(
      'The submission ID of the transaction. For userOperations, this will be the userOperation hash. For SVM transactions, this will be the transaction signature'
    ),
  transaction: z
    .string()
    .describe('For evm_userOperation: superjson serialized UserOperation'),
  transactionHash: z.string().nullish(),
  isSuccess: z.boolean().nullish(),
  actualGasCostNative: z.string().nullish(),
  actualGasCostUsd: z.string().nullish(),
  actualGasDetails: gasDetailsActualSchema.nullish(),
  quoteGasCostNative: z.string().nullish(),
  quoteGasCostUsd: z.string().nullish(),
  quoteGasDetails: gasDetailsQuoteSchema.nullish(),
  sponsorshipMode: sponsorshipModeSchema.nullish(),
  blockaidValidationResult: blockaidValidationResultSchema.nullish(),
  intents: transactionIntentsSchema.nullish(),
  events: transactionEventsSchema.nullish(),
  modifications: z.array(signingRequestModificationSchema).nullish(),
  requestedAt: z.iso.datetime(),
  confirmedAt: z.iso.datetime().nullish(),
});

// Type exports
export type Transaction = z.infer<typeof transactionSchema>;
export type TransactionStatus = Transaction['status'];
export type TransactionType = Transaction['type'];
export type GasDetailsActual = z.infer<typeof gasDetailsActualSchema>;
export type GasDetailsQuote = z.infer<typeof gasDetailsQuoteSchema>;
export type BlockaidValidationResult = z.infer<
  typeof blockaidValidationResultSchema
>;
export type Parsed = z.infer<typeof parsedSchema>;

export const submittableTransactionSchema = transactionSchema.extend({
  bannerId: z
    .string()
    .optional()
    .describe('ID of the banner that triggered this transaction'),
  transaction: z
    .string()
    .describe('For evm_userOperation: superjson serialized UserOperation'),
});
export type SubmittableTransaction = z.infer<
  typeof submittableTransactionSchema
>;

// Session-scoped transaction submission
export const submitTransactionInputSchema = submittableTransactionSchema
  .pick({
    bannerId: true,
    id: true,
    quoteGasDetails: true,
    quoteGasCostUsd: true,
    quoteGasCostNative: true,
    sponsorshipMode: true,
    walletAddress: true,
    dappSessionId: true,
    chainId: true,
    type: true,
    parsed: true,
    transaction: true,
    blockaidValidationResult: true,
    intents: true,
    events: true,
    modifications: true,
  })
  .extend({
    submitId: z.string().nullish(),
    status: z.nullish(transactionStatusSchema),
  });
export type SubmitTransactionInput = z.infer<
  typeof submitTransactionInputSchema
>;
